import { Editor, Element, Node, Range, Text, Transforms } from 'slate'
import { ReactEditor } from 'slate-react'
import { HistoryEditor } from 'slate-history'
import { UserSchema } from 'models'
import { EditorService } from '../EditorService'

export const ContentCommands = {
  ...HistoryEditor,

  getOffsetOfLastEl(el: Element) {
    return el.children && el.children.length
      ? ContentCommands.getChildOffset(el, el.children.length - 1)
      : 0
  },

  getChildOffset(el: Element, childIndex: number) {
    const { children } = el
    if (!children || children.length === 0) { return 0 }
    const child: any = children[childIndex]
    return child.text && child.text.length ? child.text.length : 0
  },

  getBlocksOfType(editor: ReactEditor, type: string|string[]) {
    return Editor.nodes(editor, { match: n => Array.isArray(type) ? type.includes(n.type as string) : n.type === type })
  },

  getSubElements(node: Node) {
    const size = (node as any).children
    return Node.nodes(node, { from: [0], to: [size.length + 1] })
  },

  isBlockType(editor: ReactEditor, type: string|string[]) {
    const [match] = ContentCommands.getBlocksOfType(editor, type)
    return !!match
  },

  isFormatActive(editor: ReactEditor, format: string): boolean {
    const [match] = Array.from(Editor.nodes(editor, { match: n => n[format] === true, mode: 'all' }))
    return !!match
  },

  toggleFlag(editor: ReactEditor, flag: string) {
    const isActive = ContentCommands.isFormatActive(editor, flag)
    Transforms.setNodes(editor, { [flag]: isActive ? null : true }, { match: Text.isText, split: true })
  },

  toggleBold(editor: ReactEditor) { ContentCommands.toggleFlag(editor, 'bold') },
  toggleItalic(editor: ReactEditor) { ContentCommands.toggleFlag(editor, 'italic') },
  toggleUnderline(editor: ReactEditor) { ContentCommands.toggleFlag(editor, 'underline') },

  clearFormatting(editor: ReactEditor) {
    Transforms.setNodes(editor, { bold: null, italic: null, underline: null }, { match: Text.isText, split: true })
  },

  setParagraphLevel(editor: ReactEditor, level: string|null) {
    if (level === 'text') { level = null }
    Transforms.setNodes(editor, { level }, { match: Text.isText, split: true })
  },

  insertImage(s: EditorService, url?: string) {
    const img: IImageElement = { type: 'image', url, children: [{ text: '' }] }
    Transforms.insertNodes(s.editor, img)
  },

  /**
   * Turns paragraph blocks into list items, or turns a list into paragraphs
   * @param s The service
   * @param type The type of list
   */
  toggleList(s: EditorService, type: ListTypes = ListTypes.bulleted) {
    const active = ContentCommands.isBlockType(s.editor, type)
    Transforms.unwrapNodes(s.editor, { match: n => LIST_TYPES.includes(n.type as ListTypes), split: true })
    Transforms.setNodes(s.editor, { type: active ? 'paragraph' : 'list-item' })
    if (!active) {
      const listWrapper = { type, children: [] }
      Transforms.wrapNodes(s.editor, listWrapper)
    }
  },

  /**
   * Handles pressing enter on a list item, either adding a new list item, or if empty, turning the item into a paragraph
   * @param s The service
   */
  enterOnList(s: EditorService) {
    const [match] = ContentCommands.getBlocksOfType(s.editor, 'list-item')
    if (Node.string(match[0]) === '') {
      ContentCommands.toggleList(s, ListTypes.bulleted)
    } else {
      const item = { type: 'list-item', children: [{ text: '' }] }
      Transforms.insertNodes(s.editor, item)
    }
  },

  // insertBulletList(s: EditorService) {
  //   const list = { type: ListTypes.bulleted, children: [{ type: 'list-item', children: [{ text: '' }] }] }
  //   Transforms.insertNodes(s.editor, list)
  // },

  // insertNumberedList(s: EditorService) {
  //   const list = { type: 'list-numbered', children: [{ type: 'list-item', children: [{ text: '' }] }] }
  //   Transforms.insertNodes(s.editor, list)
  // },

  // indentList(s: EditorService) {
  //   const listWrapper = { type: ListTypes.bulleted, children: [] }
  //   Transforms.wrapNodes(s.editor, listWrapper)
  // },

  // tabOnList(s: EditorService, reverse?: boolean) {
  //   // const [match] = ContentCommands.getBlocksOfType(s.editor, 'list-item')
  //   const listWrapper = { type: 'list-bulleted', children: [] }
  //   const listItemWrapper = { type: 'list-item', children: [] }
  //   Transforms.wrapNodes(s.editor, listItemWrapper)
  //   Transforms.wrapNodes(s.editor, listWrapper)
  // },

  addMention(user: UserSchema, s: EditorService) {
    const name = UserSchema.getNameString(user)
    const mention: IMentionElement = { type: 'mention', name, user_id: user.id, children: [{ text: '' }] }
    Transforms.insertNodes(s.editor, mention)
    Transforms.move(s.editor)
  },

  toggleLink(s: EditorService, url: string = '') {
    const { selection } = s.editor
    const isLink = ContentCommands.isBlockType(s.editor, 'link')

    if (isLink) {
      Transforms.unwrapNodes(s.editor, {
        match: n =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
      })
    } else {
      const isCollapsed = selection && Range.isCollapsed(selection)
      const link = {
        type: 'link',
        url,
        children: isCollapsed ? [{ text: url }] : [],
      }

      if (isCollapsed) {
        Transforms.insertNodes(s.editor, link)
      } else {
        Transforms.wrapNodes(s.editor, link, { split: true })
        Transforms.collapse(s.editor, { edge: 'end' })
      }
    }
  },
}

export interface IParagraphElement extends Element {}
export interface IParagraphTextLeaf extends Text {
  bold?: boolean
  italic?: boolean
  underline?: boolean
  level?: 'text'|'h1'|'h2'|'h3'
}

export interface IImageElement extends Element {
  url?: string
}

export interface ILinkElement extends Element {
  url?: string
}

export interface IMentionElement extends Element {
  name: string
  user_id: string
}

export enum ListTypes {
  numbered = 'list-numbered',
  bulleted = 'list-bulleted',
}

export enum CommonElements {
  paragraph = 'paragraph',
  listitem = 'list-item',
  listnumbered = 'list-numbered',
  listbulleted = 'list-bulleted',
}

export const LIST_TYPES = Object.values(ListTypes)
export const COMMON_ELEMENTS = Object.values(CommonElements)
